import { useEffect, useState, type ReactElement } from 'react';
import { BEREICH_FARBEN, BEREICH_FARBWERTE, BEREICH_VORSCHLAEGE, ZIELE, type Op, exportVorlageAus, neuerBereich, settingsPatch, urteil, timelineDuration } from '@schnitt/shared';
import { api, type RenderView, type VorlageView } from '../api';
import { useEditor } from '../store';
import { useZeitText } from '../zeit';
import { VorlagenSheet } from './VorlagenSheet';
import { BereichSheet } from './BereichSheet';
import { Sheet } from './Sheet';
import { InfoTipp } from './InfoTipp';

/**
 * Export und Ablieferung in den Ordner. Teilen hat ein eigenes Blatt
 * (`TeilenSheet`, Sascha, 27. Oktober: „so verschachtelt und viel Text“).
 */
export function ExportSheet({ onClose }: { onClose: () => void }): ReactElement {
  const zeit = useZeitText();
  const project = useEditor((s) => s.project);
  /**
   * Die Abschnitte der Vorschau (7.71b) sind keine Exporte. Sascha,
   * 10. September: „warum entsteht hier jedes mal ein Export? Das ist doch
   * voll falsch“ -- stimmt: sie entstehen im Hintergrund, damit die Vorschau
   * läuft, und haben in einer Liste, in der man seine Fassungen sucht, nichts
   * zu suchen.
   */
  const bereich = useEditor((s) => s.bereich);
  const setBereich = useEditor((s) => s.setBereich);
  // Ist ein Bereich gewählt, ist er auch gemeint -- wer ihn gesetzt hat, wollte
  // ihn. Abwählen kann man ihn hier trotzdem, ohne ihn aufzuheben.
  const [nurBereich, setNurBereich] = useState(true);
  const alleRenders = useEditor((s) => s.renders);
  const renders = alleRenders.filter((r) => !r.abdruck);
  const timeline = useEditor((s) => s.timeline);
  const playhead = useEditor((s) => s.playhead);
  const dispatch = useEditor((s) => s.dispatch);
  const [vorlagen, setVorlagen] = useState(false);
  /** Die Export-Vorlagen, aus denen ein Stapel wird (S29). */
  const [fassungen, setFassungen] = useState<VorlageView[]>([]);
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  useEffect(() => {
    if (vorlagen) return;
    // Nach dem Vorlagen-Blatt neu holen: dort kann eine dazugekommen sein.
    void api.vorlagen('export').then((r) => setFassungen(r.vorlagen.filter((v) => v.daten !== null))).catch(() => undefined);
  }, [vorlagen]);
  const refreshRenders = useEditor((s) => s.refreshRenders);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);

  const [busy, setBusy] = useState(false);
  const [deliverToBox, setDeliverToBox] = useState(true);
  /**
   * **Zwei Stufen** (7.251; Sascha, 15. Oktober: „Wir brauchen hier auch nur
   * eine Vorschau und eine normale Version“). Die Stufe ist gemerkt je Gerät.
   * Die Reiter „Export“ und „Teilen“ von damals sind seit dem 27. Oktober zwei
   * Blätter.
   */
  const [stufe, setStufeRoh] = useState<Stufe>(() => {
    try {
      return localStorage.getItem(STUFE_SCHLUESSEL) === 'vorschau' ? 'vorschau' : 'normal';
    } catch {
      return 'normal';
    }
  });
  const setStufe = (g: Stufe) => {
    setStufeRoh(g);
    try {
      localStorage.setItem(STUFE_SCHLUESSEL, g);
    } catch {
      /* ohne Speicher eben nur für jetzt */
    }
  };
  /** Anlegen und Verwalten der Bereiche steht hinter einem Knopf: die meisten Filme haben keine. */
  const [bereicheOffen, setBereicheOffen] = useState(false);
  /** Der eigene Name (S42) -- aufgeklappt, nicht als `window.prompt`. */
  const [eigener, setEigener] = useState(false);
  /**
   * Der Bereich, der gerade angelegt wurde -- und nach Namen und Farbe gefragt
   * wird (Sascha, 30. September: „Nach dem Anlegen von einem Bereich nach dem
   * Namen und Farbe fragen“). Sein Blatt tritt an die Stelle dieses hier, wie
   * die Vorlagen; „Fertig“ führt zurück.
   */
  const [frisch, setFrisch] = useState<string | null>(null);
  const [eigenName, setEigenName] = useState('');
  /** Welche Zeile gerade geöffnet ist, und der Name darin. */
  const [offen, setOffen] = useState<string | null>(null);
  const [umbenennen, setUmbenennen] = useState('');

  /**
   * Wohin der fertige Film geht, wenn das Projekt an einem Ordner hängt --
   * und ob es überhaupt einen gibt (7.50). Ohne Ordner keine Frage und kein
   * Kästchen.
   */
  const ordner = project?.ablage ? (project.ablage.anbieter === 'nextcloud' ? 'Nextcloud' : 'Box') : project?.boxFolderId ? 'Box' : null;

  useEffect(() => {
    const running = renders.some((r) => r.status === 'queued' || r.status === 'running');
    if (!running) return;
    // Progress also arrives over the websocket; this covers a dropped socket.
    const timer = setInterval(() => void refreshRenders(), 4000);
    return () => clearInterval(timer);
  }, [renders, refreshRenders]);

  /** Der eine Knopf. Eine Vorschau von Hand gibt es nicht mehr -- die rechnet
      der Editor selbst, in der Ruhe (7.71d). */
  const exportiere = async () => {
    if (!project) return;
    setBusy(true);
    try {
      // Die Vorschau wird aus den Proxys gerechnet, schnell und klein, und nach
      // zwei Tagen aufgeräumt; „Normal“ ist der Export in guter Qualität
      // (die Stufe „hoch“ aus 7.213) und geht, wenn gewünscht, in den Ordner.
      const vorschau = stufe === 'vorschau';
      await api.startRender(project.id, vorschau ? 'preview' : 'final', !vorschau && deliverToBox && ordner !== null, nurBereich ? bereich : null, vorschau ? 'standard' : 'hoch');
      await refreshRenders();
      const was = vorschau ? 'Vorschau' : 'Export';
      toast('info', nurBereich && bereich
        ? `${was} gestartet — nur ${zeit(bereich.von)} bis ${zeit(bereich.bis)}.`
        : `${was} gestartet.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Export konnte nicht gestartet werden.');
    } finally {
      setBusy(false);
    }
  };

  const saveWorkFile = async () => {
    if (!project) return;
    setBusy(true);
    try {
      const result = await api.saveToBox(project.id);
      toast('success', `Arbeitsdatei „${result.name}“ im Box-Ordner gespeichert.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Speichern in Box fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const duration = timelineDuration(timeline);

  /**
   * **Eine freie Wahl, kein schon benannter Bereich** (S44).
   *
   * Nach dem Anlegen bleibt der neue Bereich gewählt -- für den Export ist
   * das richtig, man will ihn meist gleich ausgeben. Aber das nächste
   * „+ Story“ nahm dann dieselbe Wahl und machte eine Kopie desselben
   * Stücks, statt am Abspielkopf anzufangen. Oben stand zwar „Neu aus dem
   * gewählten Bereich“, aber das muss man lesen. Jetzt gilt für „Neu“ nur
   * eine Wahl, die noch kein Bereich ist -- die mit I und O gesetzte.
   */
  const freieWahl =
    bereich && !timeline.exportbereiche.some((b) => Math.abs(b.von - bereich.von) < 0.001 && Math.abs(b.bis - bereich.bis) < 0.001)
      ? bereich
      : null;

  /**
   * Einen Bereich anlegen (S42).
   *
   * **Wo er anfängt, sagt der Abspielkopf** -- das war Saschas erster Satz
   * dazu („die Bereiche entstehen irgendwie nicht am Play Marker“), und
   * vorher ging es überhaupt nur aus einem gewählten Bereich heraus. Steht
   * einer da, gilt er: wer ein Stück gewählt hat, meint dieses Stück.
   *
   * **Bis wohin?** So weit, wie der Name es nahelegt: eine Story ist fünfzehn
   * Sekunden, ein Reel dreissig, ein Teaser zehn, „Outtakes“ geht bis zum
   * Ende (siehe `BEREICH_VORSCHLAEGE`). Damit liegt die rechte Kante *neben*
   * dem Abspielkopf und lässt sich sofort ziehen -- ein Band bis ans Ende des
   * Films hätte eine Kante, die man erst suchen muss. Länger als der Film
   * wird nichts. Steht der Kopf schon am Ende, wird rückwärts gegriffen,
   * sonst wäre der Bereich leer.
   */
  const legeAn = (name: string, sekunden: number | null): void => {
    const vonRoh = freieWahl ? freieWahl.von : playhead;
    const bisRoh = freieWahl ? freieWahl.bis : sekunden === null ? duration : Math.min(duration, playhead + sekunden);
    let von = Math.max(0, Math.min(vonRoh, duration));
    let bis = Math.max(0, Math.min(bisRoh, duration));
    if (bis - von < 0.2) {
      // Am Ende (oder fast) gibt es nach vorn nichts mehr zu holen.
      von = Math.max(0, Math.min(von, duration - 0.2));
      bis = Math.max(von + 0.2, Math.min(duration, von + Math.max(0.2, duration - von)));
    }
    // Angeheftet oder fest, wie es eingestellt ist (7.166; die Einstellung
    // setzt das Blatt des Bereichs mit, 30. September). Voreingestellt: an den
    // Clips -- ein Bereich soll bei ihnen bleiben, wenn davor etwas dazukommt.
    const b = { ...neuerBereich(timeline.exportbereiche, name, von, bis), angeheftet: timeline.settings.bereicheAnheften !== false };
    dispatch([{ op: 'addExportbereich', bereich: b }] as Op[]);
    setBereich({ von: b.von, bis: b.bis });
    setNurBereich(true);
    setFrisch(b.id);
  };

  /**
   * Stapel-Export (S29). Die Reihenfolge ist die des Anwählens: wer hochkant
   * zuerst braucht, bekommt es zuerst.
   */
  const stapel = async () => {
    if (!project || gewaehlt.length === 0) return;
    setBusy(true);
    try {
      const { bestellt } = await api.stapelExport(project.id, gewaehlt, deliverToBox);
      setGewaehlt([]);
      await refreshRenders();
      toast('success', `${bestellt.length} Fassung${bestellt.length === 1 ? '' : 'en'} in der Warteschlange.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Der Stapel ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  // Vorlagen (S24): Format, Bildrate und Ziellautheit unter einem Namen.
  // „Reel hochkant“ ist eine Antwort auf drei Fragen, und sie ist jedes Mal
  // dieselbe. Das Blatt tritt an die Stelle dieses hier und legt sich nicht
  // darüber: zwei Blätter übereinander sind zwei „Fertig“-Knöpfe, und der
  // obere liegt hinter dem Schleier des unteren.
  if (frisch) {
    return <BereichSheet bereichId={frisch} neu onClose={() => setFrisch(null)} />;
  }

  if (vorlagen) {
    return (
      <VorlagenSheet
        art="export"
        aktuell={exportVorlageAus(timeline.settings)}
        anwenden={(daten) => {
          if (daten.art === 'export') dispatch({ op: 'updateSettings', patch: settingsPatch(daten) } as Op);
        }}
        onClose={() => setVorlagen(false)}
      />
    );
  }

  /**
   * Ein Einzelbild von der Stelle, an der der Abspielkopf steht (S22).
   * Es geht durch dieselbe Warteschlange wie ein Export und landet in
   * derselben Liste -- dort steht es dann mit einem Knopf zum Laden.
   */
  const bildHolen = async () => {
    if (!project) return;
    setBusy(true);
    try {
      await api.einzelbild(project.id, playhead);
      await refreshRenders();
      toast('info', `Bild bei ${zeit(playhead)} wird gerechnet.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Das Bild ging nicht.');
    } finally {
      setBusy(false);
    }
  };

  const istBereich = (b: { von: number; bis: number }) =>
    !!bereich && Math.abs(bereich.von - b.von) < 0.001 && Math.abs(bereich.bis - b.bis) < 0.001;
  /** Gibt es überhaupt etwas anderes als den ganzen Film? Sonst steht die Frage nicht da. */
  const gibtBereiche = timeline.exportbereiche.length > 0 || freieWahl !== null;

  return (
    <Sheet title="Exportieren" onClose={onClose}>
      <div data-export>
        <div className="sub" style={{ marginBottom: 12 }}>
          {timeline.settings.width}×{timeline.settings.height} · {timeline.settings.fps} fps · {formatDauer(duration)}
        </div>
        {/* **Was gerechnet wird** -- nur, wenn es mehr gibt als den ganzen
            Film (7.251: „Die Bereiche nur anbieten wenn es sie wirklich
            gibt“). */}
        {gibtBereiche ? (
          <div style={{ marginBottom: 12 }} data-nur-bereich={nurBereich && bereich ? 'ja' : 'nein'}>
            <span className="sub" style={{ display: 'block', marginBottom: 6 }}>
              Was
            </span>
            <div className="chips">
              <button
                className={`chip${!(nurBereich && bereich) ? ' active' : ''}`}
                data-export-was="ganz"
                onClick={() => setNurBereich(false)}
              >
                Ganzer Film
              </button>
              {timeline.exportbereiche.map((b) => (
                <button
                  key={b.id}
                  className={`chip bereich-chip${nurBereich && istBereich(b) ? ' active' : ''}`}
                  data-exportbereich-chip={b.id}
                  data-exportbereich-farbe={b.farbe}
                  style={{ ['--bereich' as string]: BEREICH_FARBWERTE[b.farbe] }}
                  title={`${zeit(b.von)} bis ${zeit(b.bis)}`}
                  onClick={() => {
                    setBereich({ von: b.von, bis: b.bis });
                    setNurBereich(true);
                  }}
                >
                  {b.name}
                </button>
              ))}
              {freieWahl ? (
                <button
                  className={`chip${nurBereich ? ' active' : ''}`}
                  data-export-was="wahl"
                  onClick={() => setNurBereich(true)}
                >
                  Auswahl {zeit(freieWahl.von)}–{zeit(freieWahl.bis)}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        {ordner && stufe === 'normal' ? (
          <label className="spread" style={{ marginBottom: 12 }}>
            <span className="sub">Danach in den Projektordner legen ({ordner})</span>
            <input
              type="checkbox"
              checked={deliverToBox}
              onChange={(e) => setDeliverToBox(e.target.checked)}
              style={{ width: 22, height: 22, minHeight: 0 }}
            />
          </label>
        ) : null}
        {/* Stufe und Knopf bleiben unten stehen (7.213: „man muss das
            Export Modal scrollen“). */}
        <div className="sheet-aktion" data-export-aktion>
          <div className="chips" data-export-stufe={stufe} style={{ marginBottom: 8, alignItems: 'center' }}>
            {STUFEN.map((g) => (
              <button
                key={g.id}
                className={`chip ${stufe === g.id ? 'active' : ''}`}
                data-export-stufe-wahl={g.id}
                title={g.hinweis}
                onClick={() => setStufe(g.id)}
              >
                {g.name}
              </button>
            ))}
            {/* Ganz rechts: die Erklärung klappt nach links auf, und mit
                zwei Stufen statt drei stand das (i) sonst so weit links,
                dass sie aus dem Blatt ragte. */}
            <span style={{ marginLeft: 'auto' }}>
              <InfoTipp label="Was heisst Vorschau und Normal?">
                {STUFEN.map((g) => (
                  <span key={g.id} className="info-zeile">
                    <b>{g.name}</b> {g.hinweis}
                  </span>
                ))}
              </InfoTipp>
            </span>
          </div>
          <button
            className="primary"
            style={{ width: '100%', marginBottom: 0 }}
            onClick={() => void exportiere()}
            disabled={busy || role === 'viewer' || duration === 0}
          >
            {stufe === 'vorschau' ? 'Vorschau rechnen' : bereich && nurBereich ? 'Bereich exportieren' : 'Exportieren'}
          </button>
        </div>

        <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '16px 0 8px' }}>Exporte</h3>
        <div className="list">
          {renders.length === 0 ? <p className="hint">Noch keiner.</p> : null}
          {renders.map((render) => (
            <RenderRow key={render.id} render={render} onChanged={refreshRenders} />
          ))}
        </div>

        {/* Was es sonst noch gibt -- seltener gebraucht, also darunter. */}
        <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Mehr</h3>
        <button
          style={{ width: '100%', marginBottom: 8 }}
          data-einzelbild
          onClick={() => void bildHolen()}
          disabled={busy || role === 'viewer' || duration === 0}
        >
          Bild vom Abspielkopf ({zeit(playhead)})
        </button>
        {duration > 0 ? (
          <button
            style={{ width: '100%', marginBottom: 8 }}
            data-bereiche-oeffnen={bereicheOffen ? 'offen' : 'zu'}
            onClick={() => setBereicheOffen((v) => !v)}
          >
            {bereicheOffen ? 'Bereiche ausblenden' : timeline.exportbereiche.length > 0 ? `Bereiche (${timeline.exportbereiche.length}) …` : 'Bereich anlegen …'}
          </button>
        ) : null}
    {duration > 0 && bereicheOffen ? (
      <div style={{ marginBottom: 12 }} data-exportbereiche={timeline.exportbereiche.length}>
        {/* **Anlegen: ein Tipp, ein Bereich.** Wo er anfängt, sagt der
            Abspielkopf -- oder der gewählte Bereich, wenn einer dasteht.
            Was gilt, steht darunter, damit niemand raten muss. */}
        {role !== 'viewer' ? (
          <div style={{ marginTop: 8 }} data-bereich-neu={freieWahl ? 'wahl' : 'kopf'}>
            <span className="sub" style={{ display: 'block', marginBottom: 4 }}>
              Neu {freieWahl ? 'aus dem gewählten Bereich' : `ab ${zeit(playhead)}`}
            </span>
            <div className="chips">
              {BEREICH_VORSCHLAEGE.map((v) => (
                <button
                  key={v.name}
                  className="chip"
                  data-bereich-vorschlag={v.name}
                  title={freieWahl ? 'aus dem gewählten Bereich' : v.sekunden === null ? 'bis zum Ende' : `${v.sekunden} Sekunden`}
                  onClick={() => legeAn(v.name, v.sekunden)}
                >
                  <span className="bereich-punkt" style={{ background: BEREICH_FARBWERTE[v.farbe] }} aria-hidden="true" />
                  + {v.name}
                  {freieWahl || v.sekunden === null ? '' : ` · ${v.sekunden}s`}
                </button>
              ))}
              <button className="chip" data-bereich-eigener onClick={() => setEigener((v) => !v)}>
                + Eigener Name
              </button>
            </div>
            {eigener ? (
              <form
                className="row"
                style={{ marginTop: 6, gap: 6 }}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!eigenName.trim()) return;
                  legeAn(eigenName, null);
                  setEigenName('');
                  setEigener(false);
                }}
              >
                <input
                  autoFocus
                  value={eigenName}
                  maxLength={60}
                  placeholder="Name"
                  data-bereich-name-feld
                  onChange={(e) => setEigenName(e.target.value)}
                  style={{ flex: 1 }}
                />
                <button className="small" type="submit" data-bereich-name-ok disabled={!eigenName.trim()}>
                  Anlegen
                </button>
              </form>
            ) : null}
          </div>
        ) : null}
        {/* Die Liste: Farbe, Name, Zeit -- und je Bereich das, was man damit
            tut. Umbenennen und Farbe klappen *in der Zeile* auf und nicht in
            einem eigenen Blatt: es sind zwei Felder, und ein Blatt dafür zu
            öffnen kostet zwei Tipps mehr als die Sache selbst. */}
        {timeline.exportbereiche.length > 0 ? (
          <div className="list" style={{ marginTop: 8 }}>
            {timeline.exportbereiche.map((b) => (
              <div key={b.id} data-exportbereich-zeile={b.id}>
                <div className="row">
                  <span className="sub" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      className="bereich-punkt"
                      data-exportbereich-punkt={b.id}
                      style={{ background: BEREICH_FARBWERTE[b.farbe] }}
                    />
                    {b.name} — {zeit(b.von)} bis {zeit(b.bis)}
                  </span>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button
                      className="small"
                      data-exportbereich-aendern={b.id}
                      onClick={() => {
                        setOffen((v) => (v === b.id ? null : b.id));
                        setUmbenennen(b.name);
                      }}
                    >
                      Ändern
                    </button>
                    <button
                      className="small"
                      data-exportbereich-weg={b.id}
                      onClick={() => {
                        dispatch([{ op: 'removeExportbereich', bereichId: b.id }] as Op[]);
                        toast('info', `„${b.name}“ entfernt.`);
                      }}
                    >
                      Entfernen
                    </button>
                  </span>
                </div>
                {offen === b.id ? (
                  <div style={{ padding: '6px 0 10px' }} data-exportbereich-offen={b.id}>
                    <form
                      className="row"
                      style={{ gap: 6 }}
                      onSubmit={(e) => {
                        e.preventDefault();
                        const name = umbenennen.trim().slice(0, 60);
                        if (!name || name === b.name) return;
                        dispatch([{ op: 'updateExportbereich', bereichId: b.id, patch: { name } }] as Op[]);
                        toast('success', `Heisst jetzt „${name}“.`);
                      }}
                    >
                      <input
                        value={umbenennen}
                        maxLength={60}
                        data-exportbereich-name-feld={b.id}
                        onChange={(e) => setUmbenennen(e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <button className="small" type="submit" data-exportbereich-name-ok={b.id}>
                        Umbenennen
                      </button>
                    </form>
                    <div className="chips" style={{ marginTop: 6 }}>
                      {BEREICH_FARBEN.map((f) => (
                        <button
                          key={f}
                          className={`chip farbwahl${b.farbe === f ? ' active' : ''}`}
                          data-exportbereich-farbwahl={`${b.id}:${f}`}
                          aria-label={f}
                          style={{ ['--bereich' as string]: BEREICH_FARBWERTE[f] }}
                          onClick={() => dispatch([{ op: 'updateExportbereich', bereichId: b.id, patch: { farbe: f } }] as Op[])}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    ) : null}
        <button style={{ width: '100%', marginBottom: 8 }} data-export-vorlagen onClick={() => setVorlagen(true)}>
          Export-Vorlagen…
        </button>
        {/* Stapel-Export (S29). Er steht nur da, wenn es Fassungen gibt. */}
        {fassungen.length > 0 ? (
          <>
            <div className="chips" data-stapel-wahl={String(gewaehlt.length)} style={{ marginBottom: 8 }}>
              {fassungen.map((v) => (
                <button
                  key={v.id}
                  className={`chip ${gewaehlt.includes(v.id) ? 'active' : ''}`}
                  data-stapel-fassung={v.name}
                  onClick={() =>
                    setGewaehlt(gewaehlt.includes(v.id) ? gewaehlt.filter((x) => x !== v.id) : [...gewaehlt, v.id])
                  }
                >
                  {v.name}
                </button>
              ))}
            </div>
            <button
              style={{ width: '100%', marginBottom: 8 }}
              data-stapel-starten
              disabled={busy || role === 'viewer' || duration === 0 || gewaehlt.length === 0}
              onClick={() => void stapel()}
            >
              {gewaehlt.length === 0
                ? 'Fassungen wählen'
                : `${gewaehlt.length} Fassung${gewaehlt.length === 1 ? '' : 'en'} rechnen und weggehen`}
            </button>
          </>
        ) : null}
        {project?.boxFolderId && !project?.ablage ? (
          <button style={{ width: '100%', marginTop: 6 }} onClick={() => void saveWorkFile()} disabled={busy || role === 'viewer'}>
            Arbeitsdatei in Box speichern
          </button>
        ) : null}
      </div>
    </Sheet>
  );
}

/** Eine Dateigrösse in der Einheit, in der sie etwas sagt. */
function groesse(bytes: number): string {
  return bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} kB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Sekunden als Minute:Sekunde -- eine Länge liest man nicht in Kommastellen. */
function formatDauer(sekunden: number): string {
  const ganz = Math.round(sekunden);
  return `${Math.floor(ganz / 60)}:${String(ganz % 60).padStart(2, '0')}`;
}

function RenderRow({ render, onChanged }: { render: RenderView; onChanged: () => Promise<void> }): ReactElement {
  const running = render.status === 'queued' || render.status === 'running';
  const role = useEditor((s) => s.role);
  const toast = useEditor((s) => s.toast);
  const [showError, setShowError] = useState(false);
  const [busy, setBusy] = useState(false);
  // Gemessen wird immer gleich, geurteilt nach dem Ziel des Projekts (S20).
  // Steht keines da, bleibt der Rundfunk der Massstab -- die strengere Zahl,
  // und die, an der man merkt, dass es eine gibt.
  const zielLautheit = useEditor((s) => s.timeline.settings.zielLautheit);
  const ziel = ZIELE.find((z) => z.lufs === zielLautheit) ?? ZIELE[0]!;

  const retry = async () => {
    setBusy(true);
    try {
      await api.retryRender(render.id);
      await onChanged();
      toast('info', 'Wird noch einmal gerechnet, mit dem Schnitt von damals.');
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Erneut versuchen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="row" data-render-kind={render.kind} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
      <div className="spread">
        <div className="grow">
          <div className="title">
            {render.fassung ?? (render.kind === 'final' ? 'Export' : render.kind === 'bild' ? 'Bild' : 'Vorschau')} ·{' '}
            {new Date(render.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
          </div>
          <div className="sub">
            <Status render={render} />
            {render.duration_sec ? ` · ${render.duration_sec.toFixed(1)}s` : ''}
            {/* Ein Einzelbild (S22) sind ein paar Dutzend Kilobyte -- „0.0 MB“
                liest sich wie „nichts“. Unter einem Megabyte also in kB. */}
            {render.size_bytes ? ` · ${groesse(render.size_bytes)}` : ''}
            {render.box_file_id ? ' · in Box' : ''}
            {render.cleaned_at ? ' · Datei aufgeräumt' : ''}
          </div>
          {/* Tonmessgeräte (S18): was der fertige Film misst, und ob das
              reicht. Die Zahl allein sagt niemandem etwas -- „2,1 LU zu leise
              für Rundfunk“ schon. */}
          {/* `typeof` und nicht `!== null`: eine Zeile, die von einer älteren
              Fassung kommt, hat die Felder gar nicht -- und `undefined` ist
              nicht `null`. Genau daran ist der Durchgang beim ersten Lauf
              gescheitert, mit „Cannot read properties of undefined“. */}
          {typeof render.lufs === 'number' && typeof render.spitze_dbtp === 'number' ? (
            <div className="sub" data-lautheit={render.lufs.toFixed(1)}>
              {render.lufs.toFixed(1).replace('.', ',')} LUFS · Spitze{' '}
              {render.spitze_dbtp.toFixed(1).replace('.', ',')} dBTP ·{' '}
              {urteil({ lufs: render.lufs, lra: render.lra ?? 0, spitze: render.spitze_dbtp }, ziel).satz}
            </div>
          ) : null}
        </div>
        {render.status === 'ready' && render.url ? (
          <a className="chip" href={render.url} download>
            Laden
          </a>
        ) : null}
        {/* Die Untertitel neben dem Film (S21). Sie stehen nur da, wenn es sie
            gibt -- ein Knopf, der nichts holt, ist schlechter als keiner. */}
        {render.status === 'ready' && render.untertitelUrl ? (
          <a className="chip" href={render.untertitelUrl} download data-untertitel-datei-laden>
            SRT
          </a>
        ) : null}
        {running ? (
          <button className="small" onClick={() => void api.cancelRender(render.id).then(onChanged)}>
            Abbrechen
          </button>
        ) : null}
        {(render.status === 'failed' || render.status === 'cancelled') && role !== 'viewer' ? (
          <button className="small primary" onClick={() => void retry()} disabled={busy}>
            Erneut
          </button>
        ) : null}
      </div>
      {render.status === 'running' ? (
        <div className="progressbar">
          <div style={{ width: `${Math.round(render.progress * 100)}%` }} />
        </div>
      ) : null}
      {render.stillstand ? (
        <div className="sub" data-stillstand-satz>
          Seit {roughMinutes(render.stillstand.seit)} keine Bewegung
          {render.stillstand.erwartet ? ` — erwartet waren rund ${roughMinutes(render.stillstand.erwartet)}` : ''}.
          Brich ihn ab und versuch es noch einmal; bleibt es dabei, steht der Grund unter{' '}
          <b>Verwaltung → Diagnose</b>.
        </div>
      ) : render.status === 'queued' ? (
        <QueueNote render={render} />
      ) : null}
      {render.error ? (
        <div>
          {/* The last lines, not the first: ffmpeg names the actual problem at
              the very end of its output, and the beginning is preamble. */}
          <div className="sub" style={{ color: 'var(--err-text)', whiteSpace: 'pre-wrap' }}>
            {showError || render.error.length <= 260 ? render.error : `…${render.error.slice(-260)}`}
          </div>
          {render.error.length > 260 ? (
            <button className="small ghost" onClick={() => setShowError(!showError)} style={{ padding: '4px 0' }}>
              {showError ? 'Weniger' : 'Ganze Meldung'}
            </button>
          ) : null}
        </div>
      ) : null}
      {render.warnings?.length ? (
        <div className="sub" style={{ color: 'var(--warn-text)' }}>
          {render.warnings.map((w, i) => (
            <div key={i}>{w}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * What a waiting export is waiting for. The estimate is arithmetic on measured
 * exports rather than a prediction, and it is left out entirely until there is
 * something to measure -- a wrong number is worse than none.
 */
/** A wait is an estimate; minutes are as precise as an estimate deserves. */
function roughMinutes(seconds: number): string {
  if (seconds < 90) return 'gut eine Minute';
  return `etwa ${Math.round(seconds / 60)} Minuten`;
}

function QueueNote({ render }: { render: RenderView }): ReactElement {
  const queue = useEditor((s) => s.queue);
  const ahead = render.queuePosition !== null ? render.queuePosition - 1 : 0;
  // The wait comes from the server: the exports in front of this one may belong
  // to other projects, and this browser cannot see those.
  const estimate = render.queueEtaSec && render.queueEtaSec >= 30 ? render.queueEtaSec : null;

  return (
    <div className="sub">
      {ahead === 0
        ? queue.running > 0
          ? 'Wartet auf den laufenden Export.'
          : 'Wird gleich gestartet.'
        : `${ahead} Export${ahead === 1 ? '' : 'e'} noch davor.`}
      {estimate ? ` Nach bisherigem Tempo noch ${roughMinutes(estimate)} Wartezeit.` : ''}
    </div>
  );
}

function Status({ render }: { render: RenderView }): ReactElement {
  if (render.status === 'ready') return <span className="badge ok">fertig</span>;
  if (render.status === 'failed') return <span className="badge err">fehlgeschlagen</span>;
  if (render.status === 'cancelled') return <span className="badge">abgebrochen</span>;
  // 7.40b: Ein Export, der steht, sah aus wie einer, der arbeitet -- „wartet ·
  // Platz 1“ oder „0 %“, beliebig lange. (Sascha, 11. September: „Siehst du in
  // den Logs dass es hängt?“) Bei den Medien steht es längst dran; hier jetzt
  // auch. Die Schwelle rechnet mit dem gemessenen Tempo, nicht mit einem
  // Bauchgefühl -- ein langer Film darf lange rechnen.
  if (render.stillstand) {
    return (
      <span className="badge err" data-stillstand={render.stillstand.seit}>
        hängt seit {roughMinutes(render.stillstand.seit)}
      </span>
    );
  }
  if (render.status === 'queued') {
    return <span className="badge busy">wartet{render.queuePosition ? ` · Platz ${render.queuePosition}` : ''}</span>;
  }
  return <span className="badge busy">{Math.round(render.progress * 100)}%</span>;
}

type Stufe = 'vorschau' | 'normal';
const STUFE_SCHLUESSEL = 'schnitt.exportStufe';
/**
 * Zwei Stufen statt drei (7.251). „Normal“ rechnet wie „Hoch“ aus 7.213
 * (`EXPORT_STUFEN.hoch`); „Vorschau“ aus den Proxys, wie die Abschnitte der
 * Vorschau.
 */
const STUFEN: { id: Stufe; name: string; hinweis: string }[] = [
  { id: 'vorschau', name: 'Vorschau', hinweis: 'Schnell und klein, zum Anschauen. Nach zwei Tagen weg.' },
  { id: 'normal', name: 'Normal', hinweis: 'Gute Qualität, zum Veröffentlichen.' },
];
